import React, { useEffect, useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading, FormCheck } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { formatDateToString } from "go-core/components/Formatters/FormatDate";
import { FormDatePicker } from "go-form/components/FormDatePicker";
import { FormDirty } from "go-form/components/FormDirty";
import FormMoneyInput from "go-form/components/FormMoneyInput";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { useCustomErrors } from "go-form/hooks";
import { CustomValidationConfig, CustomValidationConstraint } from "go-form/services/types";
import { apiOrganization } from "../../../../../../../../../../../services/Api/Organization/apiOrganization";
import {
	InvoiceApi,
	InvoicePaymentApi,
	InvoicePaymentType,
} from "../../../../../../../../../../../services/Api/Organization/types";

interface Props {
	invoice: InvoiceApi;
	show: boolean;
	onHide: () => void;
	fetchInvoice: () => Promise<void>;
}

const InvoicePreviewPaymentsModal = ({ fetchInvoice, invoice, show, onHide }: Props) => {
	const { t } = useTranslation();
	const [loading, setLoading] = useState(false);
	const { addFlash, addSuccessFlash } = useFlash();
	const form = useForm<InvoicePaymentApi>({
		criteriaMode: "all",
		defaultValues: invoice,
	});
	const {
		handleSubmit,
		formState,
		formState: { errors },
		setError,
		register,
		control,
		setValue,
	} = form;
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);
	const paymentTypeOptions = [
		InvoicePaymentType.CASH,
		InvoicePaymentType.BARTER,
		InvoicePaymentType.COMPENSATION,
		InvoicePaymentType.ONLINE,
		InvoicePaymentType.CREDIT_CARD,
		InvoicePaymentType.BANK_TRANSFER,
		InvoicePaymentType.VOUCHER,
	];

	useEffect(() => {
		setValue("payment_date", formatDateToString(new Date()));
	}, []);

	const getPaymentTypeOptions = paymentTypeOptions.map((option) => ({
		value: option,
		label: t(`enums.invoice.payment_type.${option}`),
	}));

	const onSubmit = handleSubmit(async (data) => {
		if (!validateCustomErrors()) {
			return;
		}

		setLoading(true);
		try {
			await apiOrganization.addInvoicePayment(invoice.id, data, { include: "all" });
			await fetchInvoice();
			onHide();
			addSuccessFlash(t("lib:common.flash.saved"));
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	const inputValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
		}),
		[setErrors]
	) satisfies CustomValidationConfig;

	return (
		<Modal show={show} onHide={onHide}>
			<Modal.Header closeButton>
				<Modal.Title>{t("modules.invoice.field.add_payments.title")}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<FormDirty formState={formState} onSubmit={onSubmit}>
					<FormCheck
						type="switch"
						name="paid"
						errors={errors}
						register={register}
						label={t("modules.invoice.field.price_paid.title")}
					/>
					<FormSelectGroup
						errors={errors}
						control={control}
						options={getPaymentTypeOptions}
						label={t("modules.invoice.field.payment_type.title")}
						name="payment_type"
						isClearable={false}
						customValidationConfig={inputValidationConfig}
					/>
					<FormMoneyInput
						errors={errors}
						name="price_paid.amount"
						currency=""
						label={t("modules.invoice.field.money_amount.title")}
						control={control}
						customValidationConfig={inputValidationConfig}
					/>
					<FormDatePicker
						errors={errors}
						control={control}
						name="payment_date"
						label={t("modules.invoice.field.deadline.title")}
						customValidationConfig={inputValidationConfig}
						allowClear={false}
					/>
				</FormDirty>
			</Modal.Body>
			<Modal.Footer>
				<ButtonLoading loading={loading} onClick={onSubmit} type="submit">
					{t("lib:common.action.save")}
				</ButtonLoading>
				<Button variant="light" onClick={onHide}>
					{t("lib:common.action.cancel")}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default InvoicePreviewPaymentsModal;

// t('enums.invoice.payment_type.CASH'),
// t('enums.invoice.payment_type.BARTER'),
// t('enums.invoice.payment_type.COMPENSATION'),
// t('enums.invoice.payment_type.ONLINE'),
// t('enums.invoice.payment_type.CREDIT_CARD'),
// t('enums.invoice.payment_type.BANK_TRANSFER'),
// t('enums.invoice.payment_type.VOUCHER'),
